import React, { useState, useCallback } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useGameBalance } from './hooks/useGameBalance';
import { useMultiplier } from './hooks/useMultiplier';
import { useSounds } from './hooks/useSounds';
import { formatLilape } from './config/gameConfig';
import { DepositWithdrawPanel } from './components/DepositWithdrawPanel';

const CARD_LABELS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const WIN_RATE = 0.03; // 3% win rate
const ROUND_MULT = 1.87;
const BET_OPTIONS = [20_000_000, 50_000_000, 100_000_000, 500_000_000];
const MAX_ROUNDS = 5;

function randCard() { return Math.ceil(Math.random() * 13); }
function randCardHigherThan(prev) {
  if (prev >= 13) return prev; // Already max
  return prev + 1 + Math.floor(Math.random() * (13 - prev));
}
function randCardLowerThan(prev) {
  if (prev <= 1) return prev;
  return Math.ceil(Math.random() * (prev - 1));
}

function CardFace({ value, revealed = true, small = false }) {
  const label = value ? CARD_LABELS[value - 1] : '?';
  const color = revealed && value ? (value <= 9 ? '#E2E8F0' : '#EF4444') : '#94A3B8';
  const sz = small ? 60 : 90;
  return (
    <div style={{
      width: sz, height: sz * 1.4, borderRadius: 10, border: '2px solid rgba(255,255,255,0.2)',
      background: revealed ? 'linear-gradient(135deg,#1E293B,#0F172A)' : 'linear-gradient(135deg,#7C3AED,#4C1D95)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: small ? 22 : 36, fontWeight: 900, color,
      boxShadow: revealed ? '0 4px 16px rgba(0,0,0,0.4)' : '0 2px 8px rgba(124,58,237,0.3)',
      position: 'relative', flexShrink: 0,
    }}>
      {revealed ? label : '🂠'}
    </div>
  );
}

export function HiLoGame({ onBack }) {
  const { hapticFeedback } = useTelegram();
  const { connected, sendTransaction, address } = useTon();
  const gameBalanceHook = useGameBalance(address, sendTransaction);
  const { gameBalance, useSpin, addWinnings, canSpin } = gameBalanceHook;
  const { applyMultiplier } = useMultiplier();
  const { play: playSound } = useSounds();

  const [bet, setBet] = useState(BET_OPTIONS[0]);
  const [rounds, setRounds] = useState(3);
  const [playing, setPlaying] = useState(false);
  const [cards, setCards] = useState([]);
  const [survived, setSurvived] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const [winAmt, setWinAmt] = useState(0);
  const [showWallet, setShowWallet] = useState(false);

  const playGame = useCallback(async () => {
    if (playing) return;
    if (!canSpin(bet)) {
      hapticFeedback?.('error');
      return;
    }
    const betSuccess = await useSpin(bet, 'hilo');
    if (!betSuccess) return;

    hapticFeedback?.('medium');
    setPlaying(true);
    setOutcome(null);
    setCards([]);
    setSurvived(0);

    // 3% win rate - predetermine outcome
    const willWin = Math.random() < WIN_RATE;
    const deck = [randCard()];

    if (willWin) {
      // Win - each card is higher than previous
      for (let i = 0; i < rounds; i++) {
        deck.push(randCardHigherThan(deck[i]));
      }
    } else {
      // Lose - fail at random point
      const failAt = Math.floor(Math.random() * rounds);
      for (let i = 0; i < rounds; i++) {
        if (i < failAt) {
          deck.push(randCardHigherThan(deck[i]));
        } else if (i === failAt) {
          deck.push(randCardLowerThan(deck[i]));
        } else {
          deck.push(randCard());
        }
      }
    }

    let surviveCount = 0;
    for (let i = 0; i < rounds; i++) {
      if (deck[i + 1] > deck[i]) surviveCount++;
      else break;
    }

    let idx = 0;
    const reveal = () => {
      idx++;
      setCards(deck.slice(0, idx));
      hapticFeedback?.('light');
      if (idx <= rounds) {
        setTimeout(reveal, 600);
      } else {
        // Win ONLY if ALL rounds passed
        const won = surviveCount === rounds;
        const mult = Math.pow(ROUND_MULT, surviveCount);
        const baseGain = won ? Math.round(bet * mult) : 0;
        const gain = applyMultiplier(baseGain);

        if (gain > 0) {
          addWinnings(gain, 'hilo', bet);
        }

        setSurvived(surviveCount);
        setWinAmt(gain);
        setOutcome(won ? 'win' : 'lose');
        playSound(surviveCount === rounds ? (surviveCount >= 3 ? 'bigWin' : 'win') : 'lose');
        setPlaying(false);
        hapticFeedback?.(surviveCount === rounds ? 'heavy' : 'error');
      }
    };
    setTimeout(reveal, 300);
  }, [playing, bet, rounds, canSpin, useSpin, addWinnings, applyMultiplier, hapticFeedback, playSound]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
      padding: '16px',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '12px',
          padding: '10px 16px',
          color: '#fff',
          fontSize: '16px',
          cursor: 'pointer',
        }}>← Back</button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: '#A855F7',
          fontSize: '24px',
          margin: 0,
        }}>🃏 Hi-Lo</h1>
      </div>

      {/* Balance */}
      <div style={{
        background: 'rgba(168,85,247,0.1)',
        border: '1px solid rgba(168,85,247,0.3)',
        borderRadius: '16px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '20px',
      }}>
        <div style={{ color: '#888', fontSize: '12px' }}>GAME BALANCE</div>
        <div style={{ color: '#A855F7', fontSize: '28px', fontWeight: 'bold', marginBottom: 8 }}>
          {formatLilape(gameBalance)} LILAPE
        </div>
        <button onClick={() => setShowWallet(true)} style={{
          padding: '8px 20px', borderRadius: 8,
          background: 'linear-gradient(135deg, #10B981, #059669)',
          border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>💰 Deposit / Withdraw</button>
      </div>

      {showWallet && <DepositWithdrawPanel onClose={() => setShowWallet(false)} gameBalanceHook={gameBalanceHook} />}

      {/* Cards Display */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        minHeight: '130px',
        alignItems: 'center',
      }}>
        {cards.length === 0 ? (
          <div style={{ color: '#666', fontSize: '14px' }}>Cards will appear here</div>
        ) : (
          cards.map((c, i) => (
            <CardFace key={i} value={c} revealed={true} small={cards.length > 4} />
          ))
        )}
      </div>

      {/* Result */}
      {outcome && (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          padding: '16px',
          borderRadius: '12px',
          background: outcome === 'win' ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: outcome === 'win' ? '#00ff00' : '#ff4444',
          }}>
            {outcome === 'win' ? `🎉 WIN +${formatLilape(winAmt)}` : `😢 LOSE (${survived}/${rounds} rounds)`}
          </div>
          <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
            Multiplier: {Math.pow(ROUND_MULT, survived).toFixed(2)}×
          </div>
        </div>
      )}

      {/* Rounds Selection */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          NUMBER OF ROUNDS (each 1.87× payout)
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          {[1, 2, 3, 4, 5].map(r => (
            <button
              key={r}
              onClick={() => !playing && setRounds(r)}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                border: rounds === r ? '2px solid #A855F7' : '1px solid rgba(255,255,255,0.2)',
                background: rounds === r ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.05)',
                color: rounds === r ? '#A855F7' : '#fff',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: playing ? 0.5 : 1,
              }}
            >
              {r}
            </button>
          ))}
        </div>
        <div style={{ color: '#666', fontSize: '11px', textAlign: 'center', marginTop: '6px' }}>
          Max payout: {Math.pow(ROUND_MULT, rounds).toFixed(2)}× if all cards go higher
        </div>
      </div>

      {/* Bet Options */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          BET AMOUNT
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {BET_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => !playing && setBet(opt)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: bet === opt ? '2px solid #A855F7' : '1px solid rgba(255,255,255,0.2)',
                background: bet === opt ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.05)',
                color: bet === opt ? '#A855F7' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: playing ? 0.5 : 1,
              }}
            >
              {formatLilape(opt)}
            </button>
          ))}
        </div>
      </div>

      {/* Play Button */}
      <button
        onClick={playGame}
        disabled={playing || !canSpin(bet)}
        style={{
          width: '100%',
          padding: '18px',
          borderRadius: '14px',
          border: 'none',
          background: playing ? '#555' : canSpin(bet) ? 'linear-gradient(135deg, #A855F7, #7C3AED)' : '#333',
          color: '#fff',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: playing || !canSpin(bet) ? 'not-allowed' : 'pointer',
        }}
      >
        {playing ? '🃏 REVEALING...' : `PLAY ${formatLilape(bet)}`}
      </button>
    </div>
  );
}
